import App from "./../shared/app";
import AppState from "./../../common/appState";
import PubStore from "./../../common/stores/pubStore";
import DownloadComponent from "./../../common/components/downloadcomponent";
import APIManager from "./../../common/utilities/apimanager";
import ContentManager from "./../../common/utilities/contentmanager";
import appReader from "./templates/appReader.dust";
import Viewer from "./components/viewer";
import appReaderMixin from "../../common/components/appReaderMixin";
import globalStylingModifier from "./../../common/utilities/globalStylingModifier";

import {
  goToPublication,
  goToHomeFromReader,
  noop,
} from "./../../common/utilities/utils";

import {
  INIT_READER,
  ACCESSED_SOCIALSHARING,
} from "./../../common/analytics/analyticsevents";
import CDNAssetsDownloader from "../../common/utilities/CDNAssetsDownloader";

const AnalyticsManager =
  require(`./../../../clients/${process.env.CLIENT}/js/analyticsManager`).default;
const config =
  require(`./../../../clients/${process.env.CLIENT}/js/analyticsConfig`).default;

export default class AppReader extends App {
  contentPackageId = null;
  publicationId = null;
  history = null;

  constructor({ contentPackageId, publicationId }) {
    super();
    this.contentPackageId = contentPackageId;
    this.publicationId = publicationId;
    this.init();
    globalStylingModifier();
  }

  //initialise appState
  initAppState() {
    const analyticsManager = new AnalyticsManager(
      config,
      this.options.showBackButtonParam
    );
    this.options.isReader = true;
    this._appState = new AppState(analyticsManager, this.options);
    this._appState.initUsePdf(this.options.usePdf);
    appReaderMixin.injectNextgenAssets.call(this);
  }

  //initialise rendering
  initRender() {
    appReaderMixin.initHistory.call(this);
    this._appState._authenticatorAuthorisationController =
      this.authenticatorAuthorisationController;
    this._appState.contentPackageId = this.contentPackageId;
    this.render(appReader);
  }

  startApp() {
    this.initStores();
    this._appState.triggerEvent(INIT_READER);
  }

  attachListeners() {
    this._appState.on(
      "preferredViewer:changed",
      this._onPreferredViewerChanged.bind(this)
    );
  }

  initStores() {
    APIManager.getAdditionalDownloadTokens()
      .then((json) => {
        appReaderMixin.initCpStore.call(this, json);
      })
      .catch(noop);
  }

  onCpLoaded() {
    this.regionManager
      .setRegion(this.cpStore.CPackage.DownloadToken)
      .then(() => {
        this._appState.setContentPackage(
          this.cpStore.CPackageInfo,
          this.cpStore.CPackage.PublicationDate,
          this.regionManager.getCurrentRegion(),
          this.cpStore.CPackage.DownloadToken
        );
        this._appState._sharingUrl = this.cpStore.sharingUrl;
        this._appState._nextgenSharingUrl = this.cpStore.nextgenSharingUrl;
        this.initOrUpdatePubStore(this.publicationId, false);
      });
  }

  // call this method if pub id changes
  initOrUpdatePubStore(pubId, startFromZero = false) {
    const init = !this.pubStore;
    this.startFromZero = startFromZero;
    if (init) {
      this.pubStore = new PubStore(
        APIManager,
        ContentManager,
        this.cpStore.CPackage,
        this.contentPackageId,
        pubId,
        this.cpStore.getVersion(),
        this._appState,
        this.options.pubStore
      );
      this.pubStore.on(
        "pubStore:pubLoaded",
        this._onPublicationLoaded.bind(this)
      );
      appReaderMixin.initBridge.call(this);
    }
    this.publicationId = this.pubStore.pubId;
    this._appState.publicationId = this.publicationId;
    this.checkAuthorisation().then(() => {
      //new dowload needed when navigating from main publication to supplements to retrieve the correct
      //downloadCredentials
      const newCredentialsNeeded =
        !this._appState.cdnAssetsDownloader || this.switchContentPackages;
      if (!this._appState.cdnAssetsDownloader) {
        this._appState.cdnAssetsDownloader = new CDNAssetsDownloader(
          APIManager,
          this._appState,
          this.sessionManager
        );
      }
      if (newCredentialsNeeded) {
        this._appState.cdnAssetsDownloader
          .requestDownloadCredentials(true)
          .then(() => {
            if (init) {
              this.pubStore.loadPub();
            } else {
              this.pubStore.reloadPub(
                pubId,
                this.contentPackageId,
                this.cpStore.getVersion()
              );
            }
          });
      } else {
        if (init) {
          this.pubStore.loadPub();
        } else {
          this.pubStore.reloadPub(
            pubId,
            this.contentPackageId,
            this.cpStore.getVersion()
          );
        }
      }
    });
  }

  onPubStoreLoaded() {
    appReaderMixin.setupPublicationInAppState.call(this);

    this._appState.totalPages = this.pubStore.CPackagePages.length;

    this._appState.modifyContentPackage(this.pubStore.PublicationToLoad);

    if (!this.viewer) {
      this.initViewer();
    } else {
      this.viewer.reload();
    }
  }

  reCheckAuthorisation() {
    if (!this._appState.authorized) {
      this.reCheck = true;
      const publicationData = {
        contentPackageId: this.contentPackageId,
        publicationId: this.pubStore.pubId,
        price: this.cpStore.CPackageInfo.price,
        // pageId: this._appState.currentPage
      };
      this.downloadcomponent.validateDownload(publicationData, true, true);
    }
  }

  onAuthorizeArticleFailed() {
    if (this.viewer) {
      this.viewer.checkAlb();
      this.reCheckAuthorisation();
    } else {
      this.checkOnAuthorizeArticleFailed = true;
    }
  }

  initViewer() {
    this._appState.on("appState:authorizeArticleFailed", () => {
      this.onAuthorizeArticleFailed();
    });
    this._appState.on("appState:pageChangeNotAuthorised", () => {
      this.reCheckAuthorisation();
    });
    this._appState.contentid = this.history.contentid;
    this.history.updateLocation(
      this._appState.contentPackageId,
      this._appState._publicationId,
      this.history.page,
      0,
      this.history.contentid,
      true
    );

    this.viewer = new Viewer({
      appState: this._appState,
      pubStore: this.pubStore,
      cpStore: this.cpStore,
      regionManager: this.regionManager,
      paywall: this.paywall,
      options: this.options,
      sessionManager: this.sessionManager,
    });
    if (this.checkOnAuthorizeArticleFailed) {
      this.onAuthorizeArticleFailed();
    }
    this.viewer.on("albController:closeAlb", () => {
      this.reCheckAuthorisation();
    });
    this.viewer.initComponents();
    this.viewer.on("viewer:onRegionChanged", () => {
      goToHomeFromReader();
    });
    this.viewer.on("viewer:onLoginChanged", () => {
      this.checkAuthorisation();
    });
    this.viewer.on(
      "viewer:publicationChanged",
      this._onPublicationChanged.bind(this)
    );
  }

  checkAuthorisation() {
    return new Promise((resolve) => {
      //checkAuthorisation
      this.downloadcomponent = new DownloadComponent(
        this.authenticatorAuthorisationController,
        this.paywall,
        this.options.downloadComponent,
        APIManager,
        this._appState,
        this.errorPopup
      );
      this.downloadcomponent.on("downloadComponent:succes", () => {
        if (this.reCheck) {
          this._appState.authorized = true;
          this._appState.socialSharing = false;
          this._appState.newDownloaderFallbackMode = false;
          if (!this._appState.cdnAssetsDownloader) {
            this._appState.cdnAssetsDownloader = new CDNAssetsDownloader(
              APIManager,
              this._appState,
              this.sessionManager
            );
          }
          this._appState.cdnAssetsDownloader
            .requestDownloadCredentials(true)
            .then(() => {
              resolve("autorization succeeded");
            });
        } else {
          this._appState
            .changeCurrentPage(
              this.readerBrowsingHistory.getPageFromHistory(),
              true
            )
            .then(() => {})
            .catch(() => {})
            .then(() => {
              this._appState.authorized = true;
              this._appState.socialSharing = false;
              this._appState.newDownloaderFallbackMode = false;
              resolve("authorization succeeded");
            });
        }
      });
      this.downloadcomponent.on("downloadComponent:close", () => {
        if (!this.reCheck) {
          goToHomeFromReader();
        } else if (
          !this._appState.authorized &&
          this._appState.socialSharing &&
          this._appState.socialSharingNumberOfArticles == 0
        ) {
          goToHomeFromReader();
        }
      });
      this.downloadcomponent.on("downloadComponent:fail", () => {
        if (this._appState.socialSharing) {
          this._appState.newDownloaderFallbackMode = true;
          this._appState.triggerEvent(ACCESSED_SOCIALSHARING);
          this._appState
            .changeCurrentPage(
              this.readerBrowsingHistory.getPageFromHistory(),
              false,
              true
            )
            .then(() => {})
            .catch(() => {})
            .then(() => {
              resolve("authorization succeeded");
            });
        }
      });
      const publicationData = {
        contentPackageId: this.contentPackageId,
        publicationId: this.pubStore.pubId,
        price: this.cpStore.CPackageInfo.price,
        // pageId: this._appState.currentPage
      };
      const contentId =
        this.history.contentid ||
        this._appState.nextgenSharingArticle ||
        this.options.allowNextgenPageSharing;
      const showPaywall =
        this._appState.socialSharing && contentId ? false : true;
      this.downloadcomponent.validateDownload(
        publicationData,
        true,
        showPaywall
      );
    });
  }

  //////////

  _onPublicationLoaded() {
    this.publicationId = this.pubStore.pubId;
    this._appState.publicationId = this.publicationId;
    this._appState.pageRatio = this.pubStore.ratio;
    this._appState.replicaPageSize = this.pubStore.originalPageSize;
    this.pubIsLoaded = false;
    if (this.startFromZero) {
      this.history.updateLocation(
        this._appState.contentPackageId,
        this._appState._publicationId,
        1,
        0
      );
    }
    this.onPubStoreLoaded();
  }

  _onPublicationChanged({ publicationId, contentPackageId: cpId }) {
    this.switchContentPackages = false;
    const contentPackageId = parseInt(cpId);
    if (contentPackageId) {
      this.contentPackageId = contentPackageId;
      if (contentPackageId != this._appState.contentPackageId) {
        this.switchContentPackages = true;
      }
      this._appState.contentPackageId = contentPackageId;
    }
    if (publicationId) {
      if (this.switchContentPackages) {
        this.cpStore.loadInfo(contentPackageId).then(() => {
          this.viewer.renderLoadingLayer();
          this.initOrUpdatePubStore(publicationId, true);
        });
      } else {
        this.viewer.renderLoadingLayer();
        this.initOrUpdatePubStore(publicationId, true);
      }
    } else if (contentPackageId) {
      // horsseries
      goToPublication({ contentPackageId }, 3);
    }
  }

  _onPreferredViewerChanged(type) {
    if (
      !this.pubStore.isCurrentPublicationMain() ||
      !this.pubStore.containsReplicaAndNextgen()
    )
      return;
    const { PublicationId, ContentPackageId } =
      this.pubStore.getPublicationByType(type);
    const publicationData = {
      publicationId: PublicationId,
      contentPackageId: ContentPackageId,
    };
    goToPublication(publicationData, 3);
  }
}



// WEBPACK FOOTER //
// ./src/web/reader/appReader.js